import { useCallback } from 'react'
import {
  flip, offset, shift, useDismiss, useFloating, useInteractions, useRole
} from '@floating-ui/react'

/*
 * Floating menu anchored on a clickable cell or button. Skipping outsidePress
 * when the press lands back on the trigger lets the caller's onClick handle the
 * toggle (closing on second click) without the dismiss firing first and the
 * same click immediately reopening the menu. The trigger is the anchor's whole
 * cell (the <td> carries the onClick, not just the inner anchor span), so a
 * second press anywhere in the cell — padding included — closes instead of reopening.
 */
const useInlineFloatingMenu = ({
  anchorEl, isOpen, onClose, placement = 'bottom-start'
}) => {
  const handleOpenChange = useCallback(open => {
    if (!open)
      onClose()
  }, [onClose])

  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: handleOpenChange,
    placement,
    middleware: [offset(6), flip(), shift({ padding: 8 })],
    elements: { reference: anchorEl }
  })

  const handleOutsidePress = useCallback(event => {
    const trigger = anchorEl?.closest('td') || anchorEl
    return !trigger?.contains(event.target)
  }, [anchorEl])
  const dismissOptions = { outsidePress: handleOutsidePress, escapeKey: true }
  const dismiss = useDismiss(context, dismissOptions)
  const role = useRole(context, { role: 'dialog' })
  const { getFloatingProps } = useInteractions([dismiss, role])

  return { refs, floatingStyles, context, getFloatingProps }
}

export default useInlineFloatingMenu
